import React, {useEffect, useState} from 'react';
import {debounce} from 'lodash';
import {PaddingColor, TransformValues} from '../../utils/helper';
import {ColorIcon, CropIcon, CustomCrop, FourThree, NineSixteen, SixteenNine} from 'src/ui-components/Iconpack';
import Tracker from '../../lib/analytics/tracker';
import {EventNames, EventProperty} from '../../lib/analytics/events';
import {ColorPicker, useColor} from 'react-color-palette';
import {Button} from '../../ui-components/Button/button';
import {TransformationType} from '../../screens/TransformationV2/Transformation';

interface TransformationSubmenuProps {
    onTransformValuesChange: (values: {
        width: string;
        height: string;
        padding: boolean;
        paddingColor: PaddingColor;
        ar?: number;
    }) => void;
    transformationObject: TransformValues;
    addTransformation: (t: TransformationType) => void;
    removeTransformation: (t: TransformationType) => void;
    isBulkEdit?: boolean;
}

export default function TransformationSubmenu({
    onTransformValuesChange,
    transformationObject,
    addTransformation,
    removeTransformation,
    isBulkEdit,
}: TransformationSubmenuProps) {
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [paddingColor, setPaddingColor] = useColor(
        transformationObject.paddingColor?.padHex ? transformationObject.paddingColor?.padHex : '',
    );

    function addHeightTransformation(h: string) {
        if (h.length) {
            addTransformation(TransformationType.HEIGHT);
        } else {
            removeTransformation(TransformationType.HEIGHT);
        }
    }

    function addWidthTransformation(w: string) {
        if (w.length) {
            addTransformation(TransformationType.WIDTH);
        } else {
            removeTransformation(TransformationType.WIDTH);
        }
    }

    function addPaddingTransformation(padding: boolean) {
        if (padding) {
            addTransformation(TransformationType.PADDING);
        } else {
            removeTransformation(TransformationType.PADDING);
        }
    }

    const debounceDuration = 500;

    const allowPadding = () => {
        return (
            transformationObject.height &&
            parseInt(transformationObject.height) > 0 &&
            transformationObject.width &&
            parseInt(transformationObject.width) > 0
        );
    };

    const handlePaddingColor = () => {
        const {r, g, b} = paddingColor.rgb;
        const padColor = `${Math.round(r)}-${Math.round(g)}-${Math.round(b)}`;
        onTransformValuesChange({
            width,
            height,
            padding: true,
            paddingColor: {padRgb: padColor, padHex: paddingColor.hex},
            ar: undefined,
        });
    };

    const debouncedHandleWidthChange = debounce((value) => {
        if (value === '' || /^\d+$/.test(value)) {
            Tracker.trackEvent(EventNames.TRANSFORMATION_WIDTH_MODIFIED, {
                [EventProperty.Width]: value,
            });
            addWidthTransformation(value);
            onTransformValuesChange({width: value, height, padding: false, paddingColor: {}});
        }
    }, debounceDuration);

    const debouncedHandleHeightChange = debounce((value) => {
        if (value === '' || /^\d+$/.test(value)) {
            Tracker.trackEvent(EventNames.TRANSFORMATION_HEIGHT_MODIFIED, {
                [EventProperty.Height]: value,
            });
            addHeightTransformation(value);
            onTransformValuesChange({width, height: value, padding: false, paddingColor: {}});
        }
    }, debounceDuration);

    const handlePaddingCheck = () => {
        Tracker.trackEvent(EventNames.TRANSFORMATION_PADDING_ADDED);
        addPaddingTransformation(!transformationObject.padding);
        onTransformValuesChange({width, height, padding: !transformationObject.padding, paddingColor: {}});
    };

    const handleArChange = (ar: number) => {
        onTransformValuesChange({width, height, padding: false, paddingColor: {}, ar: ar});
    };

    const handleInputChange = (field: string, value: string) => {
        if (field === 'width') {
            setWidth(value);
            debouncedHandleWidthChange(value);
        } else if (field === 'height') {
            setHeight(value);
            debouncedHandleHeightChange(value);
        }
    };

    useEffect(() => {
        if (transformationObject.width && width === '') {
            // to change only on reassign from ''
            setWidth(transformationObject.width);
        }
        if (transformationObject.height && height === '') {
            // to change only on reassign from ''
            setHeight(transformationObject.height);
        }
    }, [transformationObject.width, transformationObject.height]);

    return (
        <>
            {!transformationObject.isSvgOrGif ? (
                <>
                    <div className='flex items-center'>
                        <div className='block cursor-pointer items-center justify-center rounded-[6px] border-[2px] border-[#4E5BA6] bg-[#717BBC] p-[12px] md:mx-[16px] md:hidden'>
                            <CropIcon color='#FFFFFF' />
                        </div>
                        <div className='flex flex-col pl-[6px] text-[14px] font-[700] text-[#FFFFFF] md:pl-0'>
                            <span>TRANSFORM</span>
                        </div>
                    </div>
                    <span className='flex flex-col gap-5 pt-6 text-[12px] font-[700] text-[#FFFFFF] md:pt-5'>
                        Resize
                    </span>
                    <div className='mt-[7px]'>
                        <div className='flex gap-[6.5px]'>
                            <input
                                type='text'
                                value={width}
                                placeholder='W: 600'
                                className='h-[45px] w-[150px] rounded-[6px] border border-[#D0D5DD] px-2 py-1 text-[12px] placeholder-[#667085] shadow placeholder:text-[12px] placeholder:font-[400] md:h-[35px] md:w-[90px]'
                                onChange={(e) => handleInputChange('width', e.target.value)}
                            />
                            <input
                                type='text'
                                value={height}
                                placeholder='H: 600'
                                className='h-[45px] w-[150px] rounded-[6px] border border-[#D0D5DD] px-2 py-1 text-[12px] placeholder-[#667085] shadow placeholder:text-[12px] placeholder:font-[400] md:h-[35px] md:w-[90px]'
                                onChange={(e) => handleInputChange('height', e.target.value)}
                            />
                        </div>
                        {!isBulkEdit && (
                            <div className='my-4 border-b border-[#667085] pb-4'>
                                <span className='text-[12px] font-[700] text-[#FFFFFF]'>Common Sizes</span>
                                <div className='mt-2 grid cursor-pointer grid-cols-4 gap-3 md:grid-cols-2'>
                                    <div onClick={() => handleArChange(1)}>
                                        <CustomCrop />
                                    </div>
                                    <div onClick={() => handleArChange(4 / 3)}>
                                        <FourThree />
                                    </div>
                                    <div onClick={() => handleArChange(16 / 9)}>
                                        <SixteenNine />
                                    </div>
                                    <div onClick={() => handleArChange(9 / 16)}>
                                        <NineSixteen />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className='mt-2 flex items-center gap-2'>
                            <input
                                type='checkbox'
                                disabled={!allowPadding()}
                                checked={transformationObject.padding ?? false}
                                onChange={handlePaddingCheck}
                                className='rounded-1 h-4 w-4 border border-[#D0D5DD] p-3'
                            />

                            <span className='text-[14px] font-[400] text-[#FFFFFF]'>
                                Add Padding{' '}
                                {!allowPadding() && <span className='text-[14px] font-[600]'> (disabled) </span>}
                            </span>
                        </div>
                        {allowPadding() && transformationObject.padding && (
                            <div className='my-5 flex flex-col justify-center text-[14px] font-[400] text-[#FFFFFF]'>
                                <span>Select Padding Colour</span>
                                <div className='my-2'>
                                    <ColorPicker
                                        color={paddingColor}
                                        onChange={setPaddingColor}
                                        hideInput={['hsv', 'rgb']}
                                        height={195}
                                        hideAlpha={true}
                                    />
                                </div>
                                <div className='flex items-center justify-center'>
                                    <Button
                                        variant='secondary'
                                        size='lg'
                                        className='flex w-[183px] items-center rounded-button text-[12px] font-[700] text-[#155EEF]'
                                        icon
                                        onClick={handlePaddingColor}>
                                        <ColorIcon />
                                        Apply padding colour
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div>
                    <div className='absolute inset-0 z-10 flex w-full flex-col items-center justify-center gap-5 bg-black bg-opacity-50'>
                        <span className='p-5 text-lg font-[700] text-[#FFFFFF]'>
                            Aspect ratio transformation is not supported for this file type.{' '}
                        </span>
                    </div>
                    <div className='flex gap-[6.5px]'>
                        <input
                            type='text'
                            placeholder='W: 600'
                            className='h-[45px] w-[150px] rounded-[6px] border border-[#D0D5DD] px-2 py-1 text-[12px] placeholder-[#667085] shadow placeholder:text-[12px] placeholder:font-[400] md:h-[35px] md:w-[90px]'
                            onChange={(e) => handleInputChange('width', e.target.value)}
                        />
                        <input
                            type='text'
                            placeholder='H: 600'
                            className='h-[45px] w-[150px] rounded-[6px] border border-[#D0D5DD] px-2 py-1 text-[12px] placeholder-[#667085] shadow placeholder:text-[12px] placeholder:font-[400] md:h-[35px] md:w-[90px]'
                            onChange={(e) => handleInputChange('height', e.target.value)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
